import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Send, AlertCircle, Eye, Calendar, Clock } from 'lucide-react';
import { PostContentCard } from '../components/posts/PostContentCard';
import { PostAIReviewCard } from '../components/posts/PostAIReviewCard';
import { PreviewCard } from '../components/posts/PreviewCard';
import { ScheduleCard } from '../components/posts/ScheduleCard';
import { TikTokComplianceCard } from '../components/posts/TikTokComplianceCard';
import { YouTubePostSettingsCard } from '../components/posts/YouTubePostSettingsCard';
import { LinkedInPostSettingsCard } from '../components/posts/LinkedInPostSettingsCard';
import { PinterestPostSettingsCard } from '../components/posts/PinterestPostSettingsCard';
import { WaitingModal } from '../components/common/WaitingModal';
import { PublishConfirmationModal } from '../components/posts/PublishConfirmationModal';
import { SaveDraftInsteadModal } from '../components/posts/SaveDraftInsteadModal';
import { ApprovalCard } from '../components/posts/ApprovalCard';
import { getPost, submitPostReview } from '../services/postService';
import { useBrands } from '../hooks/useBrands';
import { useCreatePost } from '../hooks/useCreatePost';
import { useUpdatePost } from '../hooks/useUpdatePost';
import { useToast } from '../context/ToastContext';
import type { Post } from '../types/calendar';
import type { SocialPlatform } from '../components/icons/SocialIcon';
import { auth } from '../config/firebase';
import { Alert } from '../components/common/Alert';
import { ReviewCommentModal } from '../components/common/ReviewCommentModal';
import { AIBestTimeCard } from '../components/ai/AIBestTimeCard';
import { PlatformsCard } from '../components/posts/PlatformsCard';
import { BrandsAndPlatformsCard } from '../components/posts/BrandsAndPlatformsCard';
import { MediaPostCard } from '../components/posts/MediaPostCard';
import { logger } from '../utils/logger';
import { normalizeMedia } from '../utils/mediaUtils';
import { usePublishExistingPost } from '../hooks/usePublishPost';
import { useQueryClient } from '@tanstack/react-query';
import { getPostSaveErrorMessage } from '../utils/postErrors';
import { isFutureSchedule } from '../utils/scheduleTime';

// Define Reviewer type (can be moved to a types file later)
interface Reviewer {
  id: string;
  firstName: string;
  lastName: string;
  canPublish?: boolean; // Assuming this field exists on user data
  photoURL?: string; // Changed from string | null
}

export const CreateEditPostPage: React.FC = () => {
  const { t } = useTranslation();
  const { postId } = useParams<{ postId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { showToast } = useToast();
  const isEditMode = Boolean(postId);
  
  // Hooks for post operations
  const createPostMutation = useCreatePost();
  const updatePostMutation = useUpdatePost();
  
  // Helpers to normalize API media types (IMAGE | VIDEO | DOCUMENT)
  const getApiMediaTypeFromUrl = (url: string, fallback: 'image' | 'video' | null): 'IMAGE' | 'VIDEO' | 'DOCUMENT' => {
    if (/\.pdf(\?|#|$)/i.test(url)) return 'DOCUMENT';
    const lower = (url || '').toLowerCase();
    const imageExt = /(\.png|\.jpe?g|\.webp|\.gif|\.bmp|\.tiff?|\.avif|\.svg)(\?|#|$)/i;
    const videoExt = /(\.mp4|\.mov|\.avi|\.webm|\.m4v)(\?|#|$)/i;
    if (imageExt.test(lower)) return 'IMAGE';
    if (videoExt.test(lower)) return 'VIDEO';
    if (fallback === 'video') return 'VIDEO';
    return 'IMAGE';
  };
  const getApiMediaTypeFromFile = (file: File): 'IMAGE' | 'VIDEO' | 'DOCUMENT' => {
    const mime = (file.type || '').toLowerCase();
    if (mime === 'application/pdf') return 'DOCUMENT';
    if (mime.startsWith('video/')) return 'VIDEO';
    return 'IMAGE';
  };

  // Enhanced submission handling
  const handleApiResponse = (response: any) => {
    // Extract post ID from API response
    let postId = null;
    const possiblePaths = [
      'id',
      'post.id',
      'data.id',
      'data.post.id',
      'success.post.id',
      'postId',
      '_id',
      'documentId'
    ];

    for (const path of possiblePaths) {
      const value = path.split('.').reduce((obj, key) => obj?.[key], response);
      if (value) {
        postId = value;
        break;
      }
    }

    // Smart fallback: Extract real ID from media URL if no direct ID found
    if (!postId && response.media?.url) {
      const url = response.media.url;
      
      // Try to extract a meaningful ID from the URL patterns
      // Pattern 1: Look for timestamp-based IDs (like "1749510866219")
      const timestampMatch = url.match(/\/(\d{13})-/);
      if (timestampMatch) {
        const timestamp = timestampMatch[1];
        const userIdSuffix = response.userId ? response.userId.slice(-6) : '';
        postId = userIdSuffix ? `${timestamp}_${userIdSuffix}` : timestamp;
      }
      
      // Pattern 2: Look for hash-based IDs (like "a65b7b0b")
      if (!postId) {
        const hashMatch = url.match(/-([a-f0-9]{8})\./);
        if (hashMatch && response.userId) {
          const hash = hashMatch[1];
          const userIdSuffix = response.userId.slice(-6);
          postId = `${hash}_${userIdSuffix}`;
        }
      }
    }

    if (!postId) {
      logger.error('CreateEditPostPage', 'Failed to extract post ID from response', response);
      return null;
    }

    return postId;
  };
  
  // Get any passed content from ContentCreation
  const passedState = location.state as { 
    content?: string;
    platform?: string;
    hashtags?: string[];
    brandId?: string;
    imageUrl?: string | null;
  } | undefined;
  
  // State
  const [isLoading, setIsLoading] = useState(isEditMode);
  // Derive whether to open Canva modal from URL query
  const shouldOpenCanvaOnLoad = useMemo(() => searchParams.get('modal') === 'add-from-canva', [location.search]);
  const shouldOpenPublishingOnLoad = useMemo(() => searchParams.get('modal') === 'publishing', [location.search]);
  const [content, setContent] = useState(passedState?.content || '');
  const [selectedBrand, setSelectedBrand] = useState(passedState?.brandId || '');
  const [selectedPlatforms, setSelectedPlatforms] = useState<SocialPlatform[]>(
    passedState?.platform ? [passedState.platform as SocialPlatform] : []
  );
  const [format, setFormat] = useState<'regular' | 'reel'>('regular');
  const [media, setMedia] = useState<File | null>(null);
  // Multi-images support
  const [mediaList, setMediaList] = useState<File[]>([]);
  // Existing URLs from backend when editing a multi-images post
  const [existingMediaUrls, setExistingMediaUrls] = useState<string[]>([]);
  const [mediaUrl, setMediaUrl] = useState<string | null>(passedState?.imageUrl || null);
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(passedState?.imageUrl ? 'image' : null);
  const [currentPostStatus, setCurrentPostStatus] = useState('draft');
  
  // This state will hold the UTC date string if scheduling is active, otherwise empty string or null.
  // It's set by ScheduleCard's onScheduleChange.
  const [activeScheduledAtUtc, setActiveScheduledAtUtc] = useState<string | null>(null);
  // This state will hold the original scheduledAt from the DB, for ScheduleCard initialization
  const [initialScheduledAtUtcFromDb, setInitialScheduledAtUtcFromDb] = useState<string | null>(null);

  const [showPublishConfirm, setShowPublishConfirm] = useState<{
    show: boolean;
    postId?: string;
  }>({ show: false });
  const [showSaveDraftModal, setShowSaveDraftModal] = useState<{
    show: boolean;
    missingPlatforms: SocialPlatform[];
  }>({ show: false, missingPlatforms: [] });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [initialData, setInitialData] = useState<Partial<Post> | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const publishBtnRef = useRef<HTMLButtonElement>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewDecision, setReviewDecision] = useState<'approved' | 'rejected' | null>(null);
  // Track if user has made changes after hydration (edit mode)
  const [hydrationDone, setHydrationDone] = useState(!isEditMode);
  const [hasChanges, setHasChanges] = useState(false);
  const ignoreNextChangeRef = useRef(false);
  const initialSnapshotRef = useRef<string | null>(null);

  // Build a comparable snapshot of the user-editable state
  const getComparableState = () => {
    const sortedPlatforms = [...selectedPlatforms].sort();
    const sortedExistingMediaUrls = [...existingMediaUrls].sort();
    return {
      content,
      selectedBrand,
      selectedPlatforms: sortedPlatforms,
      format,
      // Treat local files as boolean flags (we don't stringify File objects)
      hasLocalFile: Boolean(media instanceof File),
      localImagesCount: mediaList.length,
      mediaUrl: mediaUrl || null,
      mediaType: mediaType || null,
      existingMediaUrls: sortedExistingMediaUrls,
      scheduledAt: activeScheduledAtUtc || null,
      requiresApproval,
      selectedReviewerId: selectedReviewerId || null,
      tiktokSettings,
      youtubeSettings,
      linkedinSettings,
      // Exclude savedBoardName from comparison as it's temporary
      pinterestSettingsWithoutTemp: {
        title: pinterestSettings.title,
        link: pinterestSettings.link,
        selectedBoardId: pinterestSettings.selectedBoardId,
        allowComments: pinterestSettings.allowComments,
        availableBoards: pinterestSettings.availableBoards
      },
    };
  };

  // State for Approval
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [selectedReviewerId, setSelectedReviewerId] = useState<string | null>(null);
  const [reviewers, setReviewers] = useState<Reviewer[]>([]);
  const [isLoadingReviewers, setIsLoadingReviewers] = useState(false);
  const [finalReviewerInfo, setFinalReviewerInfo] = useState<{ 
    id: string; 
    name: string; 
    photoURL?: string; 
    firstName?: string; 
    lastName?: string; 
  } | null>(null);
  const [finalReviewComments, setFinalReviewComments] = useState<string | null>(null);

  // Memoize reviewers length to prevent unnecessary re-renders
  const reviewersLength = useMemo(() => reviewers.length, [reviewers]);

  // Add tab state for right column
  const [activeTab, setActiveTab] = useState<'preview' | 'ai-time-suggestions' | 'planning'>('preview');

  // AI Review states
  const [aiResponse, setAiResponse] = useState<any | null>(null);
  const [copied, setCopied] = useState(false);

  // Keep raw postMediaType to reflect backend classification (e.g., multi-images)
  const [rawPostMediaType, setRawPostMediaType] = useState<string | null>(null);

  // Function to clear all media states
  const handleClearMedia = () => {
    setMedia(null);
    setMediaUrl(null);
    setMediaType(null);
    setMediaList([]);
  };

  // TikTok settings type
  interface TikTokSettings {
    title?: string;
    allowComments: boolean;
    allowDuets: boolean;
    allowStitch: boolean;
    discloseContent: boolean;
    privacy: '' | 'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'FOLLOWER_OF_CREATOR' | 'SELF_ONLY';
    disclosureType: string[];
    userAgreedToTerms: boolean;
    isValid: boolean;
  }

  // TikTok settings
  const [tiktokSettings, setTiktokSettings] = useState<TikTokSettings>({
    title: '',
    allowComments: false,
    allowDuets: false,
    allowStitch: false,
    discloseContent: false,
    privacy: '',
    disclosureType: [],
    userAgreedToTerms: false,
    isValid: true
  });

  // YouTube settings
  const [youtubeSettings, setYoutubeSettings] = useState<{
    title: string;
    tags: string[];
    privacy: string;
  }>({
    title: '',
    tags: [],
    privacy: 'public'
  });
  
  // LinkedIn settings
  const [linkedinSettings, setLinkedinSettings] = useState<{
    organizationId: string;
    visibility: string;
  }>({
    organizationId: '',
    visibility: 'PUBLIC'
  });

  // Pinterest settings
  const [pinterestSettings, setPinterestSettings] = useState<{
    title: string;
    link: string;
    selectedBoardId: string;
    allowComments: boolean;
    availableBoards: Array<{ id: string; name: string; description?: string; }>;
    savedBoardName?: string; // Store board name for when boards are not yet loaded
  }>({
    title: '',
    link: '',
    selectedBoardId: '',
    allowComments: true,
    availableBoards: [],
    savedBoardName: undefined
  });

  // Fetch brands
  const { data: brandsData, isLoading: isLoadingBrands } = useBrands();
  const queryClient = useQueryClient();
  // Cast brands data to the correct type and memoize to prevent re-renders
  const brands = useMemo(() => (brandsData as any)?.brands || [], [brandsData]);
  
  // Get current user ID in a stable way
  const currentUserId = useMemo(() => auth.currentUser?.uid || null, [auth.currentUser?.uid]);

  // Helper: hydrate component state from a post-like object (avoids refetch when provided via navigation)
  const hydrateFromPostData = useMemo(() => (
    (postData: any) => {
      try {
        setInitialData(postData);
        const post = postData as any;

        setRawPostMediaType(post.postMediaType || post.post_media_type || null);
        // Content
        let contentValue = '';
        if (typeof post.content === 'string') contentValue = post.content;
        else if (post.content && typeof post.content === 'object' && post.content.text) contentValue = post.content.text;
        else if (post.content && typeof post.content === 'object') contentValue = JSON.stringify(post.content);
        setContent(contentValue || '');
        // Brand
        const brandId = post.brandId || post.brand_id || '';
        setSelectedBrand(brandId);
        // Platforms
        let platforms: string[] = [];
        if (post.platforms) {
          if (Array.isArray(post.platforms)) platforms = post.platforms;
          else if (typeof post.platforms === 'object' && post.platforms !== null) platforms = Object.keys(post.platforms);
        } else if (post.platform) {
          platforms = [post.platform];
        }
        setSelectedPlatforms(platforms as SocialPlatform[]);
        // Format
        const formatValue = post.format || (post.media?.type === 'video' ? 'reel' : 'regular');
        setFormat(formatValue === 'reel' ? 'reel' : 'regular');
        // Media (single or multi)
        let mediaUrlValue: string | null = null;
        let mediaTypeValue: 'image' | 'video' | null = null;
        let mediaHandledViaUrls = false;
        const isDeclaredMulti = post.postMediaType === 'multi-image' || post.postMediaType === 'multi-images' || post.postMediaType === 'multi';
        const isMultiArray = Array.isArray(post.media) && post.media.length > 1;
        // Support Firestore arrayValue shape as well
        const firestoreArray = post.media?.arrayValue?.values;
        if ((isDeclaredMulti || isMultiArray) && (Array.isArray(post.media) || Array.isArray(firestoreArray))) {
          const urls: string[] = [];
          const source = Array.isArray(post.media) ? post.media : firestoreArray;
          for (const item of source) {
            if (typeof item === 'string') { urls.push(item); continue; }
            const fields = item?.mapValue?.fields || {};
            const url = item?.url || fields?.url?.stringValue || item?.stringValue;
            const thumb = item?.thumbnailUrl || item?.thumbnail_url || item?.thumbnailUrl1 || fields?.thumbnailUrl?.stringValue;
            const rawType = item?.type || fields?.type?.stringValue || '';
            const type = rawType.toString().toLowerCase();
            const candidate = url || thumb;
            // Accept when explicit image type OR no type but URL looks like an image
            const looksLikeImage = typeof candidate === 'string' && /\.(png|jpe?g|webp|gif|bmp|tiff?|avif|svg)(\?|#|$)/i.test(candidate);
            if (candidate && (type.startsWith('image') || type === '' || type === undefined || looksLikeImage)) {
              urls.push(candidate);
            }
          }
          if (urls.length > 0) {
            setExistingMediaUrls(urls);
            setMediaUrl(null);
            setMediaType('image');
            mediaHandledViaUrls = true;
          }
        }
        if (!mediaHandledViaUrls) {
          const normalizedMedia = normalizeMedia(post.media);
          if (normalizedMedia) {
            mediaUrlValue = normalizedMedia.url;
            mediaTypeValue = normalizedMedia.type as 'image' | 'video';
          } else if (post.mediaUrl || post.media_url) {
            mediaUrlValue = post.mediaUrl || post.media_url;
            mediaTypeValue = (post.mediaType || post.media_type || (mediaUrlValue?.includes('.mp4') ? 'video' : 'image')) as 'image' | 'video';
          } else if (post.data?.media?.url) {
            mediaUrlValue = post.data.media.url;
            mediaTypeValue = (post.data.media.type || 'image') as 'image' | 'video';
          } else if (post.post?.media?.url) {
            mediaUrlValue = post.post.media.url;
            mediaTypeValue = (post.post.media.type || 'image') as 'image' | 'video';
          }
          setMediaUrl(mediaUrlValue);
          setMediaType(mediaTypeValue);
        }
        // Status and schedule
        setCurrentPostStatus(post.status || 'draft');
        const scheduleDateValue = post.scheduledAt || post.scheduled_at || post.scheduledFor || null;
        setActiveScheduledAtUtc(scheduleDateValue);
        setInitialScheduledAtUtcFromDb(scheduleDateValue);
        // Platform-specific settings
        if (post.platforms?.tiktok?.settings || post.platformSettings?.tiktok || post.tiktokSettings || post.tiktok_settings) {
          const newFormatTikTokData = post.platforms?.tiktok?.settings || {};
          const platformTikTokData = post.platformSettings?.tiktok || {};
          const legacyTikTokData = post.tiktokSettings || post.tiktok_settings || {};
          const tikTokData = { ...legacyTikTokData, ...platformTikTokData, ...newFormatTikTokData };
          let privacyValue = '' as '' | 'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'FOLLOWER_OF_CREATOR' | 'SELF_ONLY';
          const rawPrivacy = tikTokData.privacy_level || tikTokData.privacy;
          switch(rawPrivacy?.toUpperCase?.()) {
            case 'PUBLIC':
            case 'PUBLIC_TO_EVERYONE': privacyValue = 'PUBLIC_TO_EVERYONE'; break;
            case 'MUTUAL_FOLLOW_FRIENDS':
            case 'MUTUAL_FOLLOWERS': privacyValue = 'MUTUAL_FOLLOW_FRIENDS'; break;
            case 'FOLLOWER_OF_CREATOR':
            case 'FOLLOWERS': privacyValue = 'FOLLOWER_OF_CREATOR'; break;
            case 'PRIVATE':
            case 'SELF_ONLY': privacyValue = 'SELF_ONLY'; break;
            default: privacyValue = '';
          }
          setTiktokSettings({
            title: tikTokData.title || '',
            allowComments: !tikTokData.disable_comment,
            allowDuets: !tikTokData.disable_duet,
            allowStitch: !tikTokData.disable_stitch,
            discloseContent: Boolean(tikTokData.brand_safety_brand_owned || tikTokData.brand_safety_paid_partnership),
            privacy: privacyValue,
            disclosureType: [
              ...(tikTokData.brand_safety_brand_owned ? ['brand'] : []),
              ...(tikTokData.brand_safety_paid_partnership ? ['branded'] : [])
            ],
            userAgreedToTerms: tikTokData.userAgreedToTerms || tikTokData.user_agreed_to_terms || false,
            isValid: true
          });
        }
        const newFormatYouTubeData = post.platforms?.youtube?.settings;
        const platformYouTubeData = post.platformSettings?.youtube;
        const legacyYouTubeData = post.youtubeSettings || post.youtube_settings;
        if (newFormatYouTubeData || platformYouTubeData || legacyYouTubeData) {
          const ytData = { ...legacyYouTubeData, ...platformYouTubeData, ...newFormatYouTubeData };
          setYoutubeSettings({
            title: ytData.title || '',
            tags: Array.isArray(ytData.tags) ? ytData.tags : [],
            privacy: ytData.privacy || 'public'
          });
        }
        if (post.platforms?.linkedin?.settings || post.linkedinSettings || post.linkedin_settings) {
          const newFormatLinkedInData = post.platforms?.linkedin?.settings || {};
          const legacyLinkedInData = post.linkedinSettings || post.linkedin_settings || {};
          const liData = { ...legacyLinkedInData, ...newFormatLinkedInData };
          setLinkedinSettings({
            organizationId: liData.organizationId || liData.organization_id || '',
            visibility: liData.visibility || 'PUBLIC'
          });
        }

        // Handle Pinterest settings - CORRECT PATH: platformData.pinterest.settings
        if (post.platformData?.pinterest?.settings) {
          const pinterestSettings = post.platformData.pinterest.settings;

          setPinterestSettings({
            title: pinterestSettings.title || '',
            link: pinterestSettings.link || '',
            selectedBoardId: pinterestSettings.board_id || '',
            allowComments: pinterestSettings.allow_comments !== undefined ? pinterestSettings.allow_comments : true,
            availableBoards: [], // Boards will be updated by useEffect when brands load
            savedBoardName: pinterestSettings.board_name // Store board name for when boards are loaded
          });
        } else {
          // Reset to default values if no Pinterest data exists
          setPinterestSettings({
            title: '',
            link: '',
            selectedBoardId: '',
            allowComments: true,
            availableBoards: [],
            savedBoardName: undefined
          });
        }
        // Approval
        const reviewerIdValue = post.reviewerId || post.reviewer_id || null;
        const needsReviewValue = post.needsReview !== undefined ? post.needsReview : post.needs_review !== undefined ? post.needs_review : Boolean(reviewerIdValue);
        if (needsReviewValue !== undefined || reviewerIdValue) {
          setRequiresApproval(Boolean(needsReviewValue));
          setSelectedReviewerId(reviewerIdValue);
          setFinalReviewComments(post.reviewComment || post.review_comment || null);
          const reviewedStatuses = ['ready_to_publish', 'scheduled', 'published', 'rejected'];
          if (reviewerIdValue && reviewedStatuses.includes(post.status)) {
            const brandForPost = brands.find((b: any) => b.id === (post.brandId || post.brand_id));
            if (brandForPost && brandForPost.members) {
              const reviewerDetail = brandForPost.members.find((m: any) => m.id === reviewerIdValue);
              if (reviewerDetail) {
                setFinalReviewerInfo({
                  id: reviewerDetail.id,
                  name: `${reviewerDetail.firstName || ''} ${reviewerDetail.lastName || ''}`.trim(),
                  photoURL: reviewerDetail.photoURL || undefined,
                  firstName: reviewerDetail.firstName || '',
                  lastName: reviewerDetail.lastName || ''
                });
              } else {
                setFinalReviewerInfo({ id: 'unknown', name: t('common.unknownReviewer', 'Unknown Reviewer') });
              }
            } else {
              setFinalReviewerInfo({ id: 'unknown', name: t('common.unknownReviewer', 'Unknown Reviewer') });
            }
          } else {
            setFinalReviewerInfo(null);
          }
        }
      } catch (e) {
        logger.error('CreateEditPostPage', 'Failed hydrating from navigation state post', e);
      }
      // Mark hydration complete and reset change tracking
      setHydrationDone(true);
      setHasChanges(false);
      ignoreNextChangeRef.current = true; // ignore the immediate next change effect caused by hydration
      try {
        initialSnapshotRef.current = JSON.stringify(getComparableState());
      } catch {
        initialSnapshotRef.current = null;
      }
    }
  ), [brands, t]);

  // Change tracking: mark as changed when any meaningful input changes after hydration
  useEffect(() => {
    if (!hydrationDone) return;
    if (ignoreNextChangeRef.current) { 
      ignoreNextChangeRef.current = false; 
      return; 
    }
    // Compare current snapshot to initial snapshot
    try {
      const current = JSON.stringify(getComparableState());
      setHasChanges(current !== initialSnapshotRef.current);
    } catch {
      // Fallback: be conservative and do not mark as changed
      setHasChanges(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    content,
    selectedBrand,
    selectedPlatforms,
    format,
    media,
    mediaUrl,
    mediaList,
    existingMediaUrls,
    activeScheduledAtUtc,
    tiktokSettings,
    youtubeSettings,
    linkedinSettings,
    pinterestSettings,
    requiresApproval,
    selectedReviewerId
  ]);

  // Fetch post data if in edit mode
  useEffect(() => {
    if (isEditMode && postId) {
      const fetchPost = async () => {
        try {
          setIsLoading(true);
          
          // Check if user is authenticated
          const user = auth.currentUser;
          if (!user) {
            logger.error('CreateEditPostPage', 'No authenticated user');
            setError('You must be logged in to edit a post');
            setIsLoading(false);
            return;
          }
          // Prefer navigation state post to avoid an extra API call
          const statePost = (location.state as any)?.post;
          if (statePost) {
            // First hydrate from the lightweight state object for snappy UI
            hydrateFromPostData(statePost);
            // If state suggests multi-images but doesn't include the full media array, fetch full post
            const rawType = (statePost.postMediaType || statePost.post_media_type || '').toString().toLowerCase();
            const indicatesMulti = ['multi', 'multi-image', 'multi-images', 'carousel', 'carousel-post', 'album'].includes(rawType);
            const hasMultiArray = Array.isArray(statePost.media) && statePost.media.length > 1;
            if (indicatesMulti && !hasMultiArray) {
              const fullPost = await getPost(postId);
              if (fullPost) {
                hydrateFromPostData(fullPost);
              }
            }
            setIsLoading(false);
            return;
          }

          // Fallback: Fetch the post data from the API
          const postData = await getPost(postId);
          
          // Safety check for postData
          if (!postData) {
            throw new Error('Invalid post data received from API');
          }
          
          // Hydrate from fetched data (shared logic)
          hydrateFromPostData(postData);
          
        } catch (err) {
          logger.error('CreateEditPostPage', 'Error fetching post data', err);
          
          // Enhanced error reporting
          if (err instanceof Error) {
            logger.error('CreateEditPostPage', `Error details: ${err.name} - ${err.message}`, err.stack);
            
            // Check if it's a 404 error
            const is404Error = err.message && err.message.includes('404');
            
            if (is404Error) {
              // Special handling for 404 (post not found)
              setError(`Post with ID "${postId}" could not be found. It may have been deleted.`);
              
              // Show a button to go back to posts list after delay
              setTimeout(() => {
                setError(prev => prev + "\n\nYou will be redirected to the posts list in 5 seconds...");
                
                // After 5 seconds, redirect to the posts list
                setTimeout(() => {
                  navigate('/my-content-hub');
                }, 5000);
              }, 2000);
            } else {
              // Other errors
              setError(`Failed to load post: ${err.message}`);
            }
          } else {
            setError('Failed to load post: Unknown error');
          }
        } finally {
          setIsLoading(false);
          // Loading complete - removed excessive logging
        }
      };
      
      fetchPost();
    }
  }, [isEditMode, postId, navigate, location.state, hydrateFromPostData]);

  // Effect to update reviewers list when selected brand changes
  useEffect(() => {
    if (selectedBrand && brands.length > 0) {
      setIsLoadingReviewers(true);
      const brandObj = brands.find((b: any) => b.id === selectedBrand);
      
      // Fetch all potential reviewers (users with canPublish)
      const potentialReviewers = (brandObj?.members || [])
        .filter((member: any) => 
            member.status === 'active' && 
            member.permissions?.actions?.post?.canPublish === true
        )
        .map((member: any) => ({ // Map to Reviewer structure
            id: member.id,
            firstName: member.firstName || 'Unnamed',
            lastName: member.lastName || 'User',
            photoURL: member.photoURL || undefined,
        }));

      logger.info('CreateEditPostPage', `Found ${potentialReviewers.length} potential reviewers for brand ${selectedBrand}`);
      setReviewers(potentialReviewers);
      setIsLoadingReviewers(false);
    } else if (!selectedBrand) {
      // Clear reviewers if no brand is selected
      setReviewers([]);
      setSelectedReviewerId(null);
    }
  }, [selectedBrand, brands]);

  // Auto-select default brand when brands are loaded
  useEffect(() => {
    if (!selectedBrand && brands.length > 0 && !isEditMode) {
      if (brands.length === 1) {
        setSelectedBrand(brands[0].id);
      } else {
        // If multiple brands, select the one with the most connected social accounts
        const brandWithMostAccounts = brands.reduce((bestBrand: any, currentBrand: any) => {
          const currentAccountCount = (currentBrand.social_accounts || []).length;
          const bestAccountCount = (bestBrand.social_accounts || []).length;
          return currentAccountCount > bestAccountCount ? currentBrand : bestBrand;
        }, brands[0]);
        
        setSelectedBrand(brandWithMostAccounts.id);
      }
    }
  }, [brands, selectedBrand, isEditMode]);

  // Separate effect to handle reviewer selection when approval is toggled or reviewers change
  useEffect(() => {
    if (requiresApproval && reviewersLength > 0) {
      const currentReviewerIsValid = reviewers.some((r: Reviewer) => r.id === selectedReviewerId);
      
      if (!currentReviewerIsValid) {
        const newReviewerId = reviewers[0]?.id || null;
        setSelectedReviewerId(newReviewerId);
      }
    } else if (!requiresApproval) {
      setSelectedReviewerId(null);
    }
  }, [requiresApproval, reviewersLength]); // Remove problematic dependencies to prevent infinite loop

  // Handle brand selection
  const handleBrandSelect = (brandId: string) => {
    setSelectedBrand(brandId);
  };

  // Get TikTok username and profile image for the selected brand
  const getTikTokAccountInfo = useCallback(() => {
    const selectedBrandObj = brands.find((b: any) => b.id === selectedBrand);
    if (!selectedBrandObj || !selectedBrandObj.social_accounts) return { username: '', picture_url: '' };

    const tiktokAccount = selectedBrandObj.social_accounts.find(
      (account: any) => account.platform.toLowerCase() === 'tiktok'
    );

    if (!tiktokAccount) return { username: '', picture_url: '' };

    return {
      username: tiktokAccount.username || '',
      picture_url: tiktokAccount.profile_url || tiktokAccount.picture_url || ''
    };
  }, [brands, selectedBrand]);

  // Get a specific message about what's preventing publishing
  const getPublishButtonTooltip = () => {
    if (!selectedBrand) {
      return 'Select a brand to publish';
    }
    
    if (!content.trim()) {
      return 'Add content to your post';
    }
    
    if (selectedPlatforms.length === 0) {
      return 'Select at least one platform';
    }
    
    if (selectedPlatforms.includes('tiktok')) {
      if (!tiktokSettings.userAgreedToTerms) {
        return "Agree to TikTok's terms to publish";
      }

      if (!tiktokSettings.privacy) {
        return "Select a privacy level for TikTok";
      }

      // Check specifically for disclosure toggle on but no options selected
      if (tiktokSettings.discloseContent && tiktokSettings.disclosureType.length === 0) {
        return t('tooltips.publishDisabled.tiktokDisclosure', 'You need to indicate if your content promotes yourself, a third party, or both.');
      }

      if (tiktokSettings.discloseContent && !tiktokSettings.isValid) {
        return "Complete all required TikTok brand disclosure fields";
      }
    }

    // Pinterest requires a selected board
    if (selectedPlatforms.includes('pinterest') && !pinterestSettings.selectedBoardId) {
      return 'Select a Pinterest board';
    }
    
    // Add checks for approval
    if (requiresApproval) {
        if (reviewers.length === 0) {
            return "Cannot request approval: No reviewers found for this brand.";
        }
        if (!selectedReviewerId) {
            return "Select a reviewer to send for approval";
        }
    }
    
    return 'Publish post now';
  };

  // Check if the disclosure issue is the specific blocker
  const isDisclosureIssueBlocking = () => {
    return selectedPlatforms.includes('tiktok') && 
           tiktokSettings.discloseContent && 
           tiktokSettings.disclosureType.length === 0;
  };

  // Check if we can publish
  const canPublish = () => {
    if (!selectedBrand || !content.trim() || selectedPlatforms.length === 0) {
      return false;
    }

    if (selectedPlatforms.includes('tiktok')) {
      if (!tiktokSettings.userAgreedToTerms || !tiktokSettings.privacy) {
        return false;
      }

      // Also prevent publishing when disclosure is on but no options selected
      if (tiktokSettings.discloseContent && tiktokSettings.disclosureType.length === 0) {
        return false;
      }

      if (tiktokSettings.discloseContent && !tiktokSettings.isValid) {
        return false;
      }
    }

    // Pinterest requires a selected board before publishing/scheduling
    if (selectedPlatforms.includes('pinterest')) {
      if (!pinterestSettings.selectedBoardId) {
        return false;
      }
    }

    // Check approval requirements if toggled on
    if (requiresApproval) {
        if (!selectedReviewerId) {
            return false; // Cannot publish if approval required but no reviewer selected
        }
        if (reviewers.length === 0) {
             return false; // Cannot publish if approval required but no reviewers available
        }
    }

    return true;
  };

  // Get all connected social accounts for the selected brand
  const getConnectedSocialAccounts = useCallback(() => {
    const selectedBrandObj = brands.find((b: any) => b.id === selectedBrand);
    if (!selectedBrandObj || !selectedBrandObj.social_accounts) return [];

    return selectedBrandObj.social_accounts
      .filter((account: any) => account.platform && account.username)
      .map((account: any) => ({
        platform: account.platform.toLowerCase() as SocialPlatform,
        username: account.username,
        picture_url: account.profile_url || account.picture_url || '',
        followers_count: account.followers_count || 0,
        name: account.name || account.username || '',
        boards: account.boards || [] // Include boards for Pinterest accounts
      }));
  }, [brands, selectedBrand]);

  // Get Pinterest boards from connected accounts
  const getPinterestBoards = useMemo(() => {
    const connectedAccounts = getConnectedSocialAccounts();
    const pinterestAccount = connectedAccounts.find(account => account.platform === 'pinterest');

    if (!pinterestAccount || !pinterestAccount.boards) return [];

    return pinterestAccount.boards.map((board: any) => ({
      id: board.id,
      name: board.name,
      description: board.description || '',
      privacy: board.privacy,
      follower_count: board.follower_count || 0,
      pin_count: board.pin_count || 0,
      created_at: board.created_at,
      owner: board.owner,
      media: board.media
    }));
  }, [getConnectedSocialAccounts]); // Depend on the callback function

  // Function to refresh Pinterest boards from API
  const refreshPinterestBoards = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }

      const token = await user.getIdToken();

      const response = await fetch('https://api.socialness.ai/management/pinterest/boards?fresh=true', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to refresh boards: ${response.status}`);
      }

      const data = await response.json();
      console.log('Refreshed Pinterest boards:', data);

      if (data.success && data.boards && Array.isArray(data.boards)) {
        // Update the Pinterest boards in the settings
        setPinterestSettings(prev => ({
          ...prev,
          availableBoards: data.boards.map((board: any) => ({
            id: board.id,
            name: board.name,
            description: board.description || '',
            privacy: board.privacy,
            follower_count: board.follower_count || 0,
            pin_count: board.pin_count || 0,
            created_at: board.created_at,
            owner: board.owner,
            media: board.media
          }))
        }));

        console.log('Updated Pinterest boards in settings:', data.boards.length, 'boards');
      } else {
        console.warn('Invalid response format from Pinterest boards API:', data);
      }

    } catch (error) {
      console.error('Error refreshing Pinterest boards:', error);
      throw error;
    }
  }, []);

  const tiktokAccountInfo = useMemo(() => getTikTokAccountInfo(), [getTikTokAccountInfo]);
  const connectedAccounts = useMemo(() => getConnectedSocialAccounts(), [getConnectedSocialAccounts]);
  const pinterestBoards = getPinterestBoards;

  // Update Pinterest boards when brand changes (not when boards change to avoid infinite loops)
  useEffect(() => {
    setPinterestSettings(prev => {
      // Only update availableBoards, don't change other settings to avoid loops
      return {
        ...prev,
        availableBoards: pinterestBoards
      };
    });
  }, [pinterestBoards]);

  // Handle board selection when boards are loaded and we have saved data
  useEffect(() => {
    if (pinterestBoards.length > 0) {
      setPinterestSettings(prev => {
        // Don't do anything if we already have a valid selection in the current boards
        if (prev.selectedBoardId && pinterestBoards.some(board => board.id === prev.selectedBoardId)) {
          return prev;
        }

        let selectedBoardId = prev.selectedBoardId;

        // If we have a saved board name but no selected board ID, try to find the board by name
        if (!selectedBoardId && prev.savedBoardName) {
          const boardByName = pinterestBoards.find(board => board.name === prev.savedBoardName);
          if (boardByName) {
            selectedBoardId = boardByName.id;
          }
        }

        // Check if the currently selected board exists in the new boards list
        const selectedBoardExists = selectedBoardId && pinterestBoards.some(board => board.id === selectedBoardId);

        // Only auto-select first board if we don't have any board selected at all
        const shouldAutoSelectFirst = !selectedBoardId && !prev.savedBoardName;

        const finalSelectedBoardId = selectedBoardExists ? selectedBoardId :
                                    shouldAutoSelectFirst ? (pinterestBoards.length > 0 ? pinterestBoards[0].id : '') :
                                    selectedBoardId;

        // Only update if something actually changed
        if (finalSelectedBoardId !== prev.selectedBoardId ||
            prev.savedBoardName !== (selectedBoardExists ? undefined : prev.savedBoardName)) {
          return {
            ...prev,
            selectedBoardId: finalSelectedBoardId,
            savedBoardName: selectedBoardExists ? undefined : prev.savedBoardName
          };
        }

        return prev;
      });
    }
  }, [pinterestBoards]); // Only depend on pinterestBoards, not on state changes

  // Direct publish mutation (skip confirmation for non-TikTok)
  const publishExistingMutation = usePublishExistingPost();
  const publishDirectly = async (idToPublish: string) => {
    try {
      const result: any = await publishExistingMutation.mutateAsync(idToPublish);
      if (result?.success) {
        showToast(t('toasts.publishSuccess', 'Post published successfully!'), 'success');
        navigate('/my-content-hub');
        return true;
      }
      const errorMsg = result?.error || result?.message || 'Failed to publish post';
      setError(errorMsg);
      return false;
    } catch (e: any) {
      setError(e?.message || 'Failed to publish post');
      return false;
    }
  };

  // Handle brand safety disclosure type changes
  const handleDisclosureTypeChange = (type: string, checked: boolean) => {
    const updatedTypes = checked 
      ? [...tiktokSettings.disclosureType, type] 
      : tiktokSettings.disclosureType.filter(t => t !== type);
    
    setTiktokSettings({
      ...tiktokSettings,
      disclosureType: updatedTypes
    });
  };

  // Function to get missing platform connections
  const getMissingPlatformConnections = () => {
    if (!selectedBrand || selectedPlatforms.length === 0) return [];
    
    const brandObj = brands.find((b: any) => b.id === selectedBrand);
    const connectedPlatformsForCheck = (brandObj?.social_accounts || []).map((acc: any) => acc.platform.toLowerCase());
    
    return selectedPlatforms.filter(
      platform => !connectedPlatformsForCheck.includes(platform.toLowerCase())
    );
  };

  const missingPlatforms = getMissingPlatformConnections();

  // Work with Preview Card and PlatformsCard
  const getBrandById = (brandId: string) => {
    return brands.find((b: any) => b.id === brandId) || null;
  };

  const selectedBrandObj = getBrandById(selectedBrand);

  const setPublishConfirmWithLogging = async (value: { show: boolean, postId?: string }) => {
    // If we're showing the modal and have a post ID, fetch the complete post data first
    if (value.show && value.postId) {
      try {
        // Fetch the post to get the most up-to-date data including media URL
        const postData = await getPost(value.postId);
        
        // Update the media URL if it exists in the response
        if (postData && postData.media && postData.media.url) {
          setMediaUrl(postData.media.url);
        }
        
        // Now set the state to show the modal with updated data
        setShowPublishConfirm(value);
        // Reflect modal state in URL
        setModalQueryParam('publishing');
      } catch (error) {
        logger.error('CreateEditPostPage', 'Error fetching post data for confirmation', error);
        // Still show the modal even if fetching fails
        setShowPublishConfirm(value);
        setModalQueryParam('publishing');
      }
    } else {
      // Just update the state directly for hiding the modal
      setShowPublishConfirm(value);
      setModalQueryParam(null);
    }
  };

  // Open publish modal if URL contains ?modal=publishing
  useEffect(() => {
    if (shouldOpenPublishingOnLoad && isEditMode && postId && !showPublishConfirm.show && !isLoading) {
      void setPublishConfirmWithLogging({ show: true, postId });
    }
  }, [shouldOpenPublishingOnLoad, isEditMode, postId, showPublishConfirm.show, isLoading]);

  const handleSubmit = async (shouldPublish = false) => {
    try {
      setIsSaving(true);
      // Set draft saving state when saving as draft
      if (!shouldPublish) {
        setIsSavingDraft(true);
      }
      setError(null);

      if (activeScheduledAtUtc && !isFutureSchedule(activeScheduledAtUtc)) {
        throw new Error(t('cards.schedule.pastDateWarning'));
      }
      
      // --- Check for missing accounts BEFORE validation --- 
      if (shouldPublish && selectedBrand && selectedPlatforms.length > 0) {
        const brandObj = brands.find((b: any) => b.id === selectedBrand);
        const connectedPlatformsForSubmit = (brandObj?.social_accounts || []).map((acc: any) => acc.platform.toLowerCase());
        const missingAccountPlatforms = selectedPlatforms.filter(
          platform => !connectedPlatformsForSubmit.includes(platform.toLowerCase())
        );

        if (missingAccountPlatforms.length > 0) {
          logger.warn('CreateEditPostPage', `Missing account connections for platforms: ${missingAccountPlatforms.join(', ')}`);
          setShowSaveDraftModal({
            show: true,
            missingPlatforms: missingAccountPlatforms,
          });
          setIsSaving(false); // Stop loading indicator
          return; // Exit handleSubmit early
        }
      }
      // --- End Check --- 

      // Validate required fields
      if (!selectedBrand) {
        throw new Error('Please select a brand');
      }

      if (!content.trim()) {
        throw new Error('Content cannot be empty');
      }

      const includesTikTok = selectedPlatforms.includes('tiktok');
      
      // Enhanced TikTok validation
      if (shouldPublish && includesTikTok) {
        const tiktokErrors = [];
        if (!tiktokSettings.userAgreedToTerms) {
          tiktokErrors.push("You must agree to TikTok's Music Usage Confirmation");
        }
        if (!tiktokSettings.privacy) {
          tiktokErrors.push("You must select a privacy level for your TikTok post");
        }
        if (tiktokErrors.length > 0) {
          throw new Error(tiktokErrors.join('\n'));
        }
      }

      // Pinterest validation: ensure a board is selected before publishing
      if (shouldPublish && selectedPlatforms.includes('pinterest')) {
        if (!pinterestSettings.selectedBoardId) {
          throw new Error('Please select a Pinterest board to continue.');
        }
      }

      // If publishing an existing post without changes, show confirmation modal (auto-start) without saving
      if (shouldPublish && isEditMode && postId && !activeScheduledAtUtc && !hasChanges) {
        await setPublishConfirmWithLogging({ show: true, postId });
        setIsSaving(false);
        setIsSavingDraft(false);
        return;
      }

      // Prepare the base payload - match exact structure of working curl
      // Ensure format is always valid before sending to API
      const validatedFormat = (format === 'reel') ? 'reel' : 'regular';
      
      // Build platforms object with unified format - match the curl example exactly
      const platformsObject: Record<string, any> = {};
      
      if (selectedPlatforms.includes('tiktok')) {
        platformsObject.tiktok = {
          settings: {
            title: tiktokSettings.title || content.substring(0, 100),
            privacy_level: tiktokSettings.privacy,
            disable_duet: !tiktokSettings.allowDuets,
            disable_comment: !tiktokSettings.allowComments,
            disable_stitch: !tiktokSettings.allowStitch,
            brand_safety_brand_owned: tiktokSettings.disclosureType.includes('brand'),
            brand_safety_paid_partnership: tiktokSettings.disclosureType.includes('branded')
          }
        };
      }
      
      if (selectedPlatforms.includes('instagram')) {
        platformsObject.instagram = {
          settings: {
            video_cover_timestamp_ms: 1000,
            share_to_feed: true
          }
        };
      }
      
      if (selectedPlatforms.includes('youtube')) {
        platformsObject.youtube = {
          settings: {
            title: youtubeSettings.title || content.substring(0, 100),
            privacy: youtubeSettings.privacy,
            tags: youtubeSettings.tags,
            category_id: "22"
          }
        };
      }
      
      if (selectedPlatforms.includes('facebook')) {
        platformsObject.facebook = {
          settings: {
            title: content.substring(0, 100)
          }
        };
      }
      
      if (selectedPlatforms.includes('linkedin')) {
        platformsObject.linkedin = {
          settings: {
            organizationId: linkedinSettings.organizationId,
            visibility: linkedinSettings.visibility
          }
        };
      }

      if (selectedPlatforms.includes('pinterest')) {
        // Get the selected board name from available boards
        const selectedBoard = pinterestSettings.availableBoards.find(
          board => board.id === pinterestSettings.selectedBoardId
        );
        const boardName = selectedBoard?.name || '';

        platformsObject.pinterest = {
          settings: {
            title: pinterestSettings.title || content.substring(0, 100),
            link: pinterestSettings.link,
            board_id: pinterestSettings.selectedBoardId,
            board_name: boardName,
            allow_comments: pinterestSettings.allowComments
          }
        };
      }

      const basePayload: any = {
        brandId: selectedBrand,
        title: "", // Required field, empty string default
        content,
        format: validatedFormat,
        platforms: platformsObject, // New unified format
        // Fix approval fields logic - needsReview should be true if approval is required, regardless of reviewer selection
        needsReview: requiresApproval,
        reviewerId: requiresApproval ? (selectedReviewerId || "") : "",
        tags: [], // Required field, empty array default
        hashtags: [] // Add hashtags field
      };

      // Media already hosted server-side (prior uploads, Canva imports, etc.)
      // Must be merged into basePayload.media in every branch below so we don't
      // drop them when new local files are also uploaded, or when saving a draft
      // without any new file.
      const existingMediaPayload: Array<{
        url: string;
        type: 'IMAGE' | 'VIDEO' | 'DOCUMENT';
        thumbnailUrl: string;
        description: string;
      }> = [];
      if (mediaUrl && !media) {
        existingMediaPayload.push({
          url: mediaUrl,
          type: getApiMediaTypeFromUrl(mediaUrl, mediaType),
          thumbnailUrl: mediaUrl,
          description: ""
        });
      }
      if (existingMediaUrls && existingMediaUrls.length > 0) {
        for (const u of existingMediaUrls) {
          existingMediaPayload.push({
            url: u,
            type: getApiMediaTypeFromUrl(u, null),
            thumbnailUrl: u,
            description: ""
          });
        }
      }
      if (existingMediaPayload.length > 0) {
        basePayload.media = existingMediaPayload;
      }

      // Updated status and scheduling logic
      if (activeScheduledAtUtc) { // If an active schedule is set via ScheduleCard
        basePayload.status = 'scheduled';
        basePayload.scheduledAt = activeScheduledAtUtc;
      } else if (shouldPublish && requiresApproval && selectedReviewerId) {
        basePayload.status = 'waiting_for_approval';
        // needsReview and reviewerId already set above
      } else {
        basePayload.status = shouldPublish ? 'published' : 'draft';
        basePayload.scheduledAt = null; // Explicitly nullify if not actively scheduling
      }

      // **MULTI-IMAGES/FILES UPLOAD FLOW**
      if (mediaList.length > 0) {
        try {
          const user = auth.currentUser;
          if (!user) {
            throw new Error('No authenticated user for file upload');
          }
          const token = await user.getIdToken();
          const uploadedUrls: string[] = [];
          for (const img of mediaList) {
            const fd = new FormData();
            fd.append('file', img);
            if (selectedBrand) fd.append('brandId', selectedBrand);
            const resp = await fetch('https://storage.socialness.ai/upload', {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
              body: fd,
            });
            if (!resp.ok) {
              const txt = await resp.text();
              throw new Error(`File upload failed: ${resp.status} ${txt}`);
            }
            const json = await resp.json();
            const url = json.publicUrl || json.url || json.fileUrl || json.data?.url;
            if (!url) throw new Error('File upload response missing URL');
            uploadedUrls.push(url);
          }
          basePayload.media = [
            ...existingMediaPayload,
            ...uploadedUrls.map((u) => ({
              url: u,
              type: getApiMediaTypeFromUrl(u, null),
              thumbnailUrl: u,
              description: '',
            })),
          ];

          if (isEditMode && postId) {
            await updatePostMutation.mutateAsync({ postId, postData: basePayload, isFormData: false });
            if (shouldPublish && !activeScheduledAtUtc) {
              await setPublishConfirmWithLogging({ show: true, postId });
            }
          } else if (!isEditMode) {
            const newPost = await createPostMutation.mutateAsync({ postData: basePayload, isFormData: false });
            const newPostId = handleApiResponse(newPost);
            if (shouldPublish && !activeScheduledAtUtc && newPostId) {
              await setPublishConfirmWithLogging({ show: true, postId: newPostId });
            }
          }
        } catch (multiErr) {
          logger.error('CreateEditPostPage', 'Multi-images upload failed', multiErr);
          throw multiErr;
        }
      }
      // **NEW TWO-STEP SINGLE-FILE UPLOAD PROCESS FOR EDIT MODE**
      else if (isEditMode && media instanceof File) {
        
        // Step 1: Upload file to storage service first
        try {
          const user = auth.currentUser;
          if (!user) {
            throw new Error('No authenticated user for file upload');
          }

          const token = await user.getIdToken();
          const uploadFormData = new FormData();
          uploadFormData.append('file', media);
          if (selectedBrand) {
            uploadFormData.append('brandId', selectedBrand);
          }

          const uploadResponse = await fetch('https://storage.socialness.ai/upload', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
              // Don't set Content-Type header for FormData
            },
            body: uploadFormData
          });

          if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text();
            logger.error('CreateEditPostPage', 'File upload failed', errorText);
            throw new Error(`File upload failed: ${uploadResponse.status} ${errorText}`);
          }

          const uploadResult = await uploadResponse.json();

          // Extract the uploaded file URL - handle actual API response format
          const uploadedMediaUrl = uploadResult.publicUrl || uploadResult.url || uploadResult.fileUrl || uploadResult.data?.url;
          if (!uploadedMediaUrl) {
            logger.error('CreateEditPostPage', 'No URL in upload response', uploadResult);
            throw new Error('File upload response missing URL');
          }

          // Step 2: Update post with the uploaded media URL
          basePayload.media = [
            ...existingMediaPayload,
            {
              url: uploadedMediaUrl,
              type: getApiMediaTypeFromFile(media),
              thumbnailUrl: uploadedMediaUrl, // For images/videos; for PDFs we still set same URL
              description: ""
            }
          ];

          const updatedPost = await updatePostMutation.mutateAsync({ 
            postId: postId!, 
            postData: basePayload, 
            isFormData: false 
          });

          if (shouldPublish && !activeScheduledAtUtc) {
            await setPublishConfirmWithLogging({ show: true, postId: postId! });
          }

        } catch (uploadError) {
          logger.error('CreateEditPostPage', 'Two-step upload process failed', uploadError);
          throw new Error(`Failed to upload and update post: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`);
        }
      }
      // **EXISTING LOGIC FOR NON-EDIT OR NO-NEW-FILE SCENARIOS**
      else {
        // Handle CREATE mode with new file - upload first, then JSON create with media URL
        if (!isEditMode && media instanceof File && media.size > 0) {
          try {
            const user = auth.currentUser;
            if (!user) {
              throw new Error('No authenticated user for file upload');
            }

            const token = await user.getIdToken();
            const uploadFormData = new FormData();
            uploadFormData.append('file', media);
            if (selectedBrand) {
              uploadFormData.append('brandId', selectedBrand);
            }

            const uploadResponse = await fetch('https://storage.socialness.ai/upload', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`
              },
              body: uploadFormData
            });

            if (!uploadResponse.ok) {
              const errorText = await uploadResponse.text();
              logger.error('CreateEditPostPage', 'File upload failed (create)', errorText);
              throw new Error(`File upload failed: ${uploadResponse.status} ${errorText}`);
            }

            const uploadResult = await uploadResponse.json();
            const uploadedMediaUrl = uploadResult.publicUrl || uploadResult.url || uploadResult.fileUrl || uploadResult.data?.url;
            if (!uploadedMediaUrl) {
              logger.error('CreateEditPostPage', 'Upload response missing URL (create)', uploadResult);
              throw new Error('File upload response missing URL');
            }

            basePayload.media = [
              ...existingMediaPayload,
              {
                url: uploadedMediaUrl,
                type: getApiMediaTypeFromFile(media),
                thumbnailUrl: uploadedMediaUrl,
                description: ""
              }
            ];

            const newPost = await createPostMutation.mutateAsync({
              postData: basePayload,
              isFormData: false
            });

            const newPostId = handleApiResponse(newPost);
            if (shouldPublish && !activeScheduledAtUtc && newPostId) {
              await setPublishConfirmWithLogging({ show: true, postId: newPostId });
            }

          } catch (createErr) {
            logger.error('CreateEditPostPage', 'Failed to create post with uploaded media', createErr);
            throw createErr;
          }
        } else {
          // Standard JSON submission (no file, or edit mode without new file)
          
          if (isEditMode && postId) {
            const updatedPost = await updatePostMutation.mutateAsync({ 
              postId: postId, 
              postData: basePayload, 
              isFormData: false 
            });
            if (shouldPublish && !activeScheduledAtUtc) {
              await setPublishConfirmWithLogging({ show: true, postId });
            }
          } else if (!isEditMode) {
            const newPost = await createPostMutation.mutateAsync({ 
              postData: basePayload, 
              isFormData: false 
            });
            const newPostId = handleApiResponse(newPost);
            if (shouldPublish && !activeScheduledAtUtc && newPostId) {
              await setPublishConfirmWithLogging({ show: true, postId: newPostId });
            } else if (shouldPublish && !activeScheduledAtUtc && !newPostId) {
              // If we couldn't get an ID, navigate after creation
              showToast('Post created successfully', 'success');
              navigate('/my-content-hub');
              return; // Exit early to avoid the navigation logic below
            }
          }
        }
      }

      // Navigate or show toast based on action
      if (activeScheduledAtUtc) { // If scheduled
         showToast(t('toasts.scheduleSuccess', 'Post scheduled successfully!'), 'success');
         navigate('/my-content-hub');
      } else if (!shouldPublish) { // Saved as draft - stay on page and refresh snapshot
        showToast(t('toasts.draftSaved', 'Changes saved.'), 'success');
        try { initialSnapshotRef.current = JSON.stringify(getComparableState()); } catch {}
        setHasChanges(false);
        setCurrentPostStatus('draft');
      } else if (shouldPublish && requiresApproval && selectedReviewerId) { // Sent for approval
        showToast(t('toasts.approvalRequested', 'Post sent for approval successfully!'), 'success');
        navigate('/my-content-hub');
      }
      // If shouldPublish is true but not scheduled and not for approval, publish modal handles navigation.

    } catch (err: any) {
      logger.error('Submission error:', err);
      
      setError(getPostSaveErrorMessage(err, t));
    } finally {
      setIsSaving(false);
      setIsSavingDraft(false);
    }
  };

  // Function to handle saving draft from the new modal
  const handleSaveDraftFromModal = async () => {
    // Set draft saving state
    setIsSavingDraft(true);
    try {
      // Re-use handleSubmit logic, but force it to save as draft
      await handleSubmit(false);
      // Show toast message
      showToast(t('toasts.draftSavedDueToMissingAccounts', 'Post saved as draft due to missing account connections.'), 'info');
    } finally {
      setIsSavingDraft(false);
    }
  };

  // Add format change handler
  const handleFormatChange = (newFormat: 'regular' | 'reel') => {
    setFormat(newFormat);
  };

  // Add PostContentCard specific props
  const handlePostContentFormatChange = (newFormat: 'regular' | 'reel') => {
    setFormat(newFormat);
  };

  // Updated handleScheduleChange to set activeScheduledAtUtc
  const handleScheduleChange = (newUtcDateString: string | null, timezone?: string) => {
    setActiveScheduledAtUtc(newUtcDateString);
    // Optionally, if the parent needs to know the timezone chosen in ScheduleCard, handle `timezone` here.
  };

  // Handle platform toggles for PlatformsCard
  const handlePlatformToggle = (platform: SocialPlatform) => {
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
    }
  };

  // Helper to update URL query param without navigation
  const setModalQueryParam = (value: string | null) => {
    const url = new URL(window.location.href);
    if (value) url.searchParams.set('modal', value);
    else url.searchParams.delete('modal');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  };

  // Add publisher confirmation handlers
  const handlePublishConfirm = () => {
    try {
      navigate('/my-content-hub');
    } catch (error) {
      logger.error('CreateEditPostPage', 'Error navigating after publish', error);
    }
  };

  // Prepare YouTube settings with correct types
  const youtubeSettingsWithCorrectTypes = {
    ...youtubeSettings,
    privacy: youtubeSettings.privacy as 'public' | 'private' | 'unlisted'
  };

  // Handler for submitting the review comment modal
  const handleReviewSubmit = async (comment: string) => {
    if (!postId || !reviewDecision) {
      logger.error('CreateEditPostPage', 'Missing postId or review decision for submission');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await submitPostReview(postId, reviewDecision, comment);
      showToast(
        t('toasts.reviewSubmitSuccess', 'Review submitted successfully!'),
        'success'
      );
      // Navigate back to the content hub or appropriate list after successful submission
      navigate('/my-content-hub'); // Or potentially use -1 if appropriate
    } catch (error) {
      logger.error('CreateEditPostPage', `Error submitting ${reviewDecision} review for post ${postId}`, error);
      showToast(
        error instanceof Error ? error.message : t('toasts.reviewSubmitError', 'Failed to submit review'),
        'error'
      );
    } finally {
      setIsReviewModalOpen(false); // Close modal regardless of outcome
      setIsSaving(false);
      setReviewDecision(null); // Reset decision
    }
  };

  // AI Review functions
  const handleCopyContent = () => {
    if (aiResponse) {
      navigator.clipboard.writeText(aiResponse.enhancedContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleApplyAiContent = () => {
    if (aiResponse) {
      setContent(aiResponse.enhancedContent);
      setAiResponse(null);
    }
  };

  // Determine if the current user is the reviewer for this post
  const currentUser = auth.currentUser;
  // Ensure initialData and status are checked safely
  const isReviewer = isEditMode && 
                     initialData && // Check if initialData exists
                     (initialData as any).status === 'waiting_for_approval' && // Cast to any for status check
                     currentUser?.uid === (initialData as any).reviewerId; // Cast to any for reviewerId access

  // Determine button text dynamically
  let publishButtonText = t('actions.publishNow', 'Publish Now');
  if (requiresApproval && selectedReviewerId) {
    publishButtonText = t('actions.sendForApproval', 'Send for Approval');
  } else if (activeScheduledAtUtc) { // If a schedule is active
    publishButtonText = t('actions.schedule', 'Schedule'); 
  }

  if (isLoading) {
    return (
      // Skeleton Loader for the whole page
      <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0 animate-pulse">
        {/* Skeleton Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
          <div className="w-[95%] mx-auto px-4 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-200 rounded-lg"></div> {/* Back button */} 
                <div>
                  <div className="h-6 w-48 bg-gray-200 rounded mb-1"></div> {/* Title */} 
                  <div className="h-4 w-64 bg-gray-200 rounded"></div> {/* Subtitle */} 
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="h-10 w-28 bg-gray-200 rounded-lg"></div> {/* Save button */} 
                <div className="h-10 w-32 bg-gray-200 rounded-lg"></div> {/* Publish button */} 
              </div>
            </div>
          </div>
        </div>

        {/* Skeleton Main Content */}
        <div className="w-[95%] mx-auto py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
            {/* Skeleton Left Column */}
            <div className="space-y-4 lg:space-y-6 col-span-1 xl:col-span-2 min-w-0">
              {/* Skeleton Brand/Schedule Grid */} 
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                <div className="h-48 bg-gray-200 rounded-xl"></div> {/* Brand/Platform Card */} 
                <div className="h-48 bg-gray-200 rounded-xl"></div> {/* Schedule Card */} 
              </div>
              {/* Skeleton AI Card */}
              <div className="h-64 bg-gray-200 rounded-xl"></div>
              {/* Skeleton Approval Card */}
              <div className="h-40 bg-gray-200 rounded-xl"></div>
              {/* Skeleton Content Card */} 
              <div className="h-60 bg-gray-200 rounded-xl"></div>
            </div>

            {/* Skeleton Right Column */}
            <div className="xl:col-span-1">
              <div className="sticky top-28 space-y-6">
                 {/* Skeleton Preview Card */}
                <div className="h-96 bg-gray-200 rounded-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="w-[95%] mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:text-primary-600 transition-colors"
                aria-label={t('common.back', 'Back')}
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 font-title">
                  {isEditMode 
                    ? t('pages.editPost.title', 'Edit Post') 
                    : t('pages.createPost.title', 'Create Post')}
                </h1>
                <p className="text-sm text-gray-500">
                  {isEditMode 
                    ? t('pages.editPost.subtitle', 'Update your social media post') 
                    : t('pages.createPost.subtitle', 'Create a new social media post')}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* Conditional Buttons for Reviewer */}
              {isReviewer ? (
                <>
                  <button
                    onClick={() => handleSubmit(false)} // Allow reviewer to save draft updates?
                    className="flex-1 sm:flex-initial inline-flex items-center px-4 py-2 text-primary-600 bg-white border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                    disabled={isSaving}
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {t('common.saveDraft', 'Save Draft')}
                  </button>
                  <button
                    onClick={() => { setReviewDecision('rejected'); setIsReviewModalOpen(true); }}
                    className="flex-1 sm:flex-initial inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                    disabled={isSaving}
                  >
                    {t('actions.reject', 'Reject')}
                  </button>
                  <button
                    onClick={() => { setReviewDecision('approved'); setIsReviewModalOpen(true); }}
                    className="flex-1 sm:flex-initial inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                    disabled={isSaving}
                  >
                    {t('actions.approve', 'Approve')}
                  </button>
                </>
              ) : (
                <> {/* Original Buttons */}
                  <button
                    onClick={() => handleSubmit(false)}
                    className="flex-1 sm:flex-initial inline-flex items-center px-4 py-2 text-primary-600 bg-white border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                    disabled={isSaving}
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {t('common.saveDraft', 'Save Draft')}
                  </button>
                  <div className="relative">
                    <button
                      ref={publishBtnRef}
                      onClick={() => handleSubmit(true)}
                      onMouseOver={() => setShowTooltip(true)}
                      onMouseOut={() => setShowTooltip(false)}
                      onFocus={() => setShowTooltip(true)}
                      onBlur={() => setShowTooltip(false)}
                      className={`flex-1 sm:flex-initial inline-flex items-center px-4 py-2 ${
                        canPublish()
                          ? 'bg-primary-600 hover:bg-primary-700'
                          : 'bg-gray-300 cursor-not-allowed opacity-50'
                      } text-white rounded-lg transition-colors`}
                      disabled={isSaving || !canPublish()}
                    >
                      <Send className={`w-5 h-5 mr-2 ${!canPublish() ? 'opacity-50' : ''}`} />
                      {publishButtonText}
                    </button>
                    
                    {showTooltip && !canPublish() && isDisclosureIssueBlocking() && (
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-50 w-64">
                         <Alert 
                           type="warning"
                           size="xs"
                           message="tooltips.publishDisabled.tiktokDisclosure"
                         />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Missing Platform Connections Warning */}
          {missingPlatforms.length > 0 && selectedBrandObj && (
            <div className="mt-4 flex justify-end">
              <div className="text-red-600 text-sm font-medium text-right">
                <div>
                  {t('warnings.noAccountConnectedForBrandMultiple', '{{brandName}} does not have connected accounts for: {{platformList}}.', {
                    brandName: selectedBrandObj.name,
                    platformList: missingPlatforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')
                  })}
                </div>
                <div className="mt-1">
                  {t('warnings.saveAsDraftOnlyNote', 'You can save as draft but not publish yet.')}
                </div>
              </div>
            </div>
          )}

          {/* Pinterest board selection warning */}
          {selectedPlatforms.includes('pinterest') && !pinterestSettings.selectedBoardId && (
            <div className="mt-2 flex justify-end">
              <div className="text-red-600 text-sm font-medium text-right">
                {t('warnings.pinterestNoBoardSelected', 'Please select a Pinterest board to continue.')}
              </div>
            </div>
          )}

          {/* Tab Navigation in Header */}
          <div className="flex justify-end border-b border-gray-200 mt-4 -mb-4">
            <button
              onClick={() => setActiveTab('preview')}
              className={`px-6 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === 'preview'
                  ? 'text-primary-600 border-primary-600'
                  : 'text-black border-transparent hover:text-primary-600'
              }`}
            >
              <Eye className="w-4 h-4" />
              {t('tabs.preview', 'Preview')}
            </button>
            <button
              onClick={() => setActiveTab('ai-time-suggestions')}
              className={`px-6 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === 'ai-time-suggestions'
                  ? 'text-primary-600 border-primary-600'
                  : 'text-black border-transparent hover:text-primary-600'
              }`}
            >
              <Clock className="w-4 h-4" />
              {t('tabs.aiTimeSuggestions', 'AI Time Suggestions')}
            </button>
            <button
              onClick={() => setActiveTab('planning')}
              className={`px-6 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === 'planning'
                  ? 'text-primary-600 border-primary-600'
                  : 'text-black border-transparent hover:text-primary-600'
              }`}
            >
              <Calendar className="w-4 h-4" />
              {t('tabs.planning', 'Planning')}
            </button>
          </div>

          {error && (
             <div className="mt-4">
               <Alert 
                 type="error"
                 message={error}
                 className="text-sm"
               />
             </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="w-[95%] mx-auto py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
          {/* Left Column */}
          <div className="space-y-4 lg:space-y-6 col-span-1 xl:col-span-2 min-w-0">
            {/* Updated Left Column Structure - Post Content first */}
            <div className="space-y-4 lg:space-y-6 col-span-1 xl:col-span-2 min-w-0">
              {/* Post Content - Moved to the top */}
              <div className="min-w-0">
                <PostContentCard
                  content={content}
                  onContentChange={setContent}
                  media={media}
                  onMediaChange={setMedia}
                  existingMediaUrl={mediaUrl}
                  existingMediaType={mediaType}
                  onClearMedia={handleClearMedia}
                  status={currentPostStatus}
                  format={format}
                  onFormatChange={setFormat}
                  brandId={selectedBrand}
                  selectedPlatforms={selectedPlatforms}
                  aiResponse={aiResponse}
                  onAiResponseChange={setAiResponse}
                />
              </div>

              {/* AI Review Card - Only show if AI analysis exists */}
              {aiResponse && (
                <div className="min-w-0">
                  <PostAIReviewCard
                    aiResponse={aiResponse}
                    copied={copied}
                    onCopyContent={handleCopyContent}
                    onApplyAiContent={handleApplyAiContent}
                  />
                </div>
              )}

              {/* Grid container for Brand/Platforms + LinkedIn (left) and Media (right) */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 xl:gap-6">
                {/* Left column: stack Brands/Platforms and LinkedIn settings */}
                <div className="min-w-0 space-y-4">
                  <BrandsAndPlatformsCard
                    selectedBrandId={selectedBrand}
                    onBrandSelect={handleBrandSelect}
                    selectedPlatforms={selectedPlatforms}
                    onPlatformToggle={handlePlatformToggle}
                    onFormatChange={setFormat}
                    format={format}
                    connectedAccounts={connectedAccounts}
                    tiktokAccount={tiktokAccountInfo}
                  />
                  {selectedPlatforms.includes('linkedin') && (
                    <LinkedInPostSettingsCard
                      organizationId={linkedinSettings.organizationId}
                      visibility={linkedinSettings.visibility}
                      selectedBrandId={selectedBrand}
                      onOrganizationIdChange={(orgId) => setLinkedinSettings({ ...linkedinSettings, organizationId: orgId })}
                      onVisibilityChange={(visibility) => setLinkedinSettings({ ...linkedinSettings, visibility })}
                    />
                  )}
                  {selectedPlatforms.includes('pinterest') && (
                    <PinterestPostSettingsCard
                      title={pinterestSettings.title}
                      link={pinterestSettings.link}
                      selectedBoardId={pinterestSettings.selectedBoardId}
                      allowComments={pinterestSettings.allowComments}
                      availableBoards={pinterestSettings.availableBoards}
                      onTitleChange={(title) => setPinterestSettings(prev => ({ ...prev, title }))}
                      onLinkChange={(link) => setPinterestSettings(prev => ({ ...prev, link }))}
                      onBoardChange={(boardId) => setPinterestSettings(prev => ({ ...prev, selectedBoardId: boardId }))}
                      onAllowCommentsChange={(allow) => setPinterestSettings(prev => ({ ...prev, allowComments: allow }))}
                      onRefreshBoards={refreshPinterestBoards}
                    />
                  )}
                </div>

                {/* Right column: Media */}
                <div className="min-w-0">
                  <MediaPostCard
                    media={media}
                    onMediaChange={setMedia}
                    onClearMedia={() => setMedia(null)}
                    existingMediaUrl={mediaUrl}
                    existingMediaType={mediaType}
                    existingMediaUrls={existingMediaUrls}
                    onExistingMediaUrlsChange={setExistingMediaUrls}
                    brandId={selectedBrand}
                    content={content}
                    selectedPlatforms={selectedPlatforms}
                    onExistingMediaChange={(url) => {
                      if (url) {
                        setMediaUrl(url);
                        setMediaType('image'); // Generated images are always images
                      } else {
                        setMediaUrl(null);
                        setMediaType(null);
                      }
                    }}
                    currentImageStyle={(() => {
                      // Determine brand's preferred image style when brand is selected
                      const validStyles = new Set(['auto','realistic','design','3d','artistic','modern','ad']);
                      const brand = selectedBrandObj as any;
                      const style = (brand?.imageStyle || '').toString().toLowerCase();
                      return validStyles.has(style) ? style : 'auto';
                    })()}
                    allowMultipleImages
                    multipleMedia={mediaList}
                    onMultipleImagesChange={setMediaList}
                    postMediaTypeTag={rawPostMediaType}
                    onCanvaModalToggle={(open) => setModalQueryParam(open ? 'add-from-canva' : null)}
                    openCanvaModalOnLoad={shouldOpenCanvaOnLoad}
                    onSaveDraft={async () => {
                      // Save as draft without navigating away
                      try {
                        await handleSubmit(false);
                        return true;
                      } catch {
                        return false;
                      }
                    }}
                  />
                </div>
              </div>

              {/* Platform-specific Settings Cards */}
              {selectedPlatforms.includes('tiktok') && (
                <div className="min-w-0">
                  <TikTokComplianceCard
                    title={tiktokSettings.title}
                    tiktokUsername={tiktokAccountInfo.username}
                    tiktokProfileImage={tiktokAccountInfo.picture_url}
                    allowComments={tiktokSettings.allowComments}
                    allowDuets={tiktokSettings.allowDuets}
                    allowStitch={tiktokSettings.allowStitch}
                    discloseContent={tiktokSettings.discloseContent}
                    privacy={tiktokSettings.privacy}
                    disclosureType={tiktokSettings.disclosureType}
                    userAgreedToTerms={tiktokSettings.userAgreedToTerms}
                    onTitleChange={(title) => setTiktokSettings({ ...tiktokSettings, title })}
                    onAllowCommentsChange={(allow) => setTiktokSettings({ ...tiktokSettings, allowComments: allow })}
                    onAllowDuetsChange={(allow) => setTiktokSettings({ ...tiktokSettings, allowDuets: allow })}
                    onAllowStitchChange={(allow) => setTiktokSettings({ ...tiktokSettings, allowStitch: allow })}
                    onDiscloseContentChange={(disclose, type) => setTiktokSettings({ 
                      ...tiktokSettings, 
                      discloseContent: disclose,
                      disclosureType: type ? type.split(',') : [] 
                    })}
                    onPrivacyChange={(privacy) => setTiktokSettings({ ...tiktokSettings, privacy })}
                    onUserAgreedToTermsChange={(agreed) => setTiktokSettings({ ...tiktokSettings, userAgreedToTerms: agreed })}
                    onValidationChange={(isValid) => setTiktokSettings({ ...tiktokSettings, isValid })}
                  />
                </div>
              )}

              {selectedPlatforms.includes('youtube') && (
                <div className="min-w-0">
                  <YouTubePostSettingsCard
                    title={youtubeSettings.title}
                    tags={youtubeSettings.tags}
                    privacy={youtubeSettings.privacy as 'public' | 'private' | 'unlisted'}
                    onTitleChange={(title) => setYoutubeSettings({ ...youtubeSettings, title })}
                    onTagsChange={(tags) => setYoutubeSettings({ ...youtubeSettings, tags })}
                    onPrivacyChange={(privacy) => setYoutubeSettings({ ...youtubeSettings, privacy })}
                  />
                </div>
              )}



              {/* LinkedIn settings moved above, directly below the Brands & Platforms card */}
            </div>
          </div>

          {/* Right Column - Tab Content Only */}
          <div className="xl:col-span-1">
            {/* Sticky container for tab content */}
            <div className="sticky top-28">
              {/* Tab Content - Direct content without card wrapper */}
              <div className="space-y-6">
                {activeTab === 'preview' && (
                  <PreviewCard
                    content={content}
                    selectedPlatforms={selectedPlatforms}
                    media={media}
                    multiImages={mediaList}
                    existingMediaUrls={existingMediaUrls}
                    existingMediaUrl={mediaUrl}
                    existingMediaType={mediaType}
                    format={format}
                    selectedBrand={selectedBrand}
                    brandName={selectedBrandObj?.name}
                    brandLogo={selectedBrandObj?.logo}
                    brands={brands}
                    hideEngagementCounts
                  />
                )}

                {activeTab === 'ai-time-suggestions' && (
                  <AIBestTimeCard
                    brandId={selectedBrand}
                    selectedPlatforms={selectedPlatforms}
                    brandName={selectedBrandObj?.name}
                  />
                )}

                {activeTab === 'planning' && (
                  <div className="space-y-6">
                    <ScheduleCard
                      initialScheduledAtUtc={initialScheduledAtUtcFromDb}
                      onScheduleChange={setActiveScheduledAtUtc}
                    />

                    <ApprovalCard
                      requiresApproval={requiresApproval}
                      selectedReviewerId={selectedReviewerId}
                      reviewers={reviewers}
                      onApprovalToggle={setRequiresApproval}
                      onReviewerChange={setSelectedReviewerId}
                      isLoadingReviewers={isLoadingReviewers}
                      currentUserId={currentUserId}
                      actualPostStatus={currentPostStatus}
                      finalReviewer={finalReviewerInfo}
                      finalReviewComments={finalReviewComments}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Waiting Modal */}
      <WaitingModal
        isOpen={isSaving}
        title="Saving your post"
        message="Please wait while we save your post..."
        videoSrc="/Panda Riding Horse for GIF.mov"
        gifUrl="https://media.socialness.ai/loading/unicorn.gif"
      />

      {/* Publish Confirmation */}
      {(() => {
        // Log the brand object being passed to the modal right before rendering
        return (
          <PublishConfirmationModal
            isOpen={showPublishConfirm.show}
            postId={showPublishConfirm.postId || ''}
            onClose={() => {
              void setPublishConfirmWithLogging({ show: false });
            }}
            onConfirm={handlePublishConfirm}
            content={content}
            platforms={selectedPlatforms}
            brand={selectedBrandObj as any}
            hasMedia={Boolean(media || mediaUrl || (existingMediaUrls && existingMediaUrls.length > 0) || (mediaList && mediaList.length > 0))}
            mediaType={mediaType || undefined}
            format={format}
            scheduledAt={activeScheduledAtUtc}
            media={media}
            mediaUrl={mediaUrl}
            multiImages={mediaList}
            existingMediaUrls={existingMediaUrls}
            connectedAccounts={connectedAccounts}
            autoStart={!activeScheduledAtUtc && !selectedPlatforms.includes('tiktok')}
          />
        );
      })()}
      
      {/* Save Draft Instead Modal */}
      <SaveDraftInsteadModal
        isOpen={showSaveDraftModal.show}
        onClose={() => setShowSaveDraftModal({ show: false, missingPlatforms: [] })}
        onSaveDraft={handleSaveDraftFromModal}
        missingPlatforms={showSaveDraftModal.missingPlatforms}
        brandName={selectedBrandObj?.name}
      />
      
      {/* Review Comment Modal */}
      <ReviewCommentModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        onSubmit={handleReviewSubmit}
        decision={reviewDecision}
      />
    </div>
  );
};
